
import Vue from "vue";
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Debounce from '@/decorators/debounce';
import api, { SessionAgreementSettings } from '../service/api';
import { UserAccountData } from '../model/UserAccountData';

@Component
export default class Agreements extends Vue {
    @Prop({ required: true })
    public sessionId!: string;

    public agreeSocial = false;
    public agreeEmail = false;
    public agreeSms = false;
    public agreePhysical = false;
    public agreeWebsite = false;
    public websiteUrl = '';
    public showConsentModal = false;
    public customPlatformName = '';
    public platforms = {
        facebook: false,
        instagram: false,
        tiktok: false,
        pinterest: false,
        flickr: false,
        snapchat: false,
        px500: false,
        oneX: false,
        inna: false
    };
    public loadingDraft = false;
    public savingDraft = false;
    public hasPersistedDraft = false;
    public accountData: UserAccountData | null = null;
    public renderedTextSnapshot: string | null = null;
    public consentRenderedContent = '';
    private renderPreviewLoading = false;
    private hydratingDraft = false;
    private suppressAutosave = false;
    private lastPersistedSettingsKey = '';

    async mounted() {
        await Promise.all([
            this.loadDraft(),
            this.loadAccountData(),
        ]);
    }

    get hasAnyConsentSelected() {
        // Check for incomplete consents (enabled but invalid)
        const hasIncompleteWebsiteConsent = this.agreeWebsite && !this.isValidWebsiteUrl(this.websiteUrl);
        const hasIncompleteSocialConsent = this.agreeSocial && !this.hasValidPlatformSelected();
        
        // If there are any incomplete consents, disable the button
        if (hasIncompleteWebsiteConsent || hasIncompleteSocialConsent) {
            return false;
        }
        
        // For social media consent, check if enabled AND at least one platform is selected
        const hasSocialWithPlatforms = this.agreeSocial && this.hasValidPlatformSelected();
        
        // For website consent, check if enabled AND valid website URL is provided
        const hasWebsiteWithUrl = this.agreeWebsite && this.isValidWebsiteUrl(this.websiteUrl);
        
        // Other marketing consents work independently
        const hasOtherMarketing = this.agreeEmail || this.agreeSms || this.agreePhysical;
        
        return hasSocialWithPlatforms || hasWebsiteWithUrl || hasOtherMarketing;
    }

    get isWebsiteConsentValid() {
        return this.agreeWebsite && this.isValidWebsiteUrl(this.websiteUrl);
    }

    get isSocialConsentValid() {
        return this.agreeSocial && this.hasValidPlatformSelected();
    }

    get hasRequiredAccountData(): boolean {
        if (!this.accountData) {
            return false;
        }

        const hasBase = !!this.accountData.email;
        const entity = this.accountData.businessEntity;
        if (!entity || !entity.type || !entity.data) {
            return false;
        }

        if (entity.type === 'PERSON') {
            return hasBase
                && !!this.accountData.firstName
                && !!this.accountData.lastName
                && !!entity.data.address?.street
                && !!entity.data.address?.city
                && !!entity.data.address?.postCode
                && !!entity.data.address?.country;
        }

        if (entity.type === 'SOLE_PROPRIETORSHIP') {
            return hasBase
                && !!this.accountData.firstName
                && !!this.accountData.lastName
                && !!entity.data.name
                && !!entity.data.taxNumber
                && !!entity.data.address?.street
                && !!entity.data.address?.city
                && !!entity.data.address?.postCode
                && !!entity.data.address?.country
                && !!entity.data.deliveryAddress?.street
                && !!entity.data.deliveryAddress?.city
                && !!entity.data.deliveryAddress?.postCode
                && !!entity.data.deliveryAddress?.country;
        }

        return hasBase
            && !!entity.data.name
            && !!entity.data.taxNumber
            && !!entity.data.regon
            && !!entity.data.krs
            && !!entity.data.districtCourt
            && !!entity.data.commercialDivision
            && !!entity.data.address?.street
            && !!entity.data.address?.city
            && !!entity.data.address?.postCode
            && !!entity.data.address?.country
            && !!entity.data.deliveryAddress?.street
            && !!entity.data.deliveryAddress?.city
            && !!entity.data.deliveryAddress?.postCode
            && !!entity.data.deliveryAddress?.country;
    }

    /** We need the agreement template to exist before we can render anything. Backend always returns it. */
    get canOpenAgreementForm(): boolean {
        return this.hasAnyConsentSelected && this.hasRequiredAccountData;
    }

    async loadAccountData() {
        this.accountData = await api.getUserData();
    }

    async loadDraft() {
        this.loadingDraft = true;
        this.hydratingDraft = true;
        try {
            const draft = await api.getSessionAgreementDraft(this.sessionId);
            this.applySettings(draft.settings);
            this.lastPersistedSettingsKey = JSON.stringify(draft.settings || {});
            this.hasPersistedDraft = !!draft.currentVersion;
            this.renderedTextSnapshot = draft.renderedTextSnapshot || null;
        } finally {
            this.hydratingDraft = false;
            this.loadingDraft = false;
        }
    }

    applySettings(settings: SessionAgreementSettings) {
        this.agreeSocial = !!settings.agreeSocial;
        this.agreeEmail = !!settings.agreeEmail;
        this.agreeSms = !!settings.agreeSms;
        this.agreePhysical = !!settings.agreePhysical;
        this.agreeWebsite = !!settings.agreeWebsite;
        this.websiteUrl = settings.websiteUrl || '';
        this.customPlatformName = settings.customPlatformName || '';
        this.platforms = {
            facebook: !!settings.platforms?.facebook,
            instagram: !!settings.platforms?.instagram,
            tiktok: !!settings.platforms?.tiktok,
            pinterest: !!settings.platforms?.pinterest,
            flickr: !!settings.platforms?.flickr,
            snapchat: !!settings.platforms?.snapchat,
            px500: !!settings.platforms?.px500,
            oneX: !!settings.platforms?.oneX,
            inna: !!settings.platforms?.inna,
        };
    }

    buildSettingsPayload(): SessionAgreementSettings {
        return {
            agreeSocial: this.agreeSocial,
            agreeEmail: this.agreeEmail,
            agreeSms: this.agreeSms,
            agreePhysical: this.agreePhysical,
            agreeWebsite: this.agreeWebsite,
            websiteUrl: this.websiteUrl,
            customPlatformName: this.customPlatformName,
            platforms: { ...this.platforms },
        };
    }

    async saveDraft(silent = false, bypassRequirements = false): Promise<boolean> {
        if (!bypassRequirements && !this.canOpenAgreementForm) {
            if (!silent) {
                (this as any).$vToastify.warning({
                    title: 'Uzupełnij dane',
                    body: 'Aby zapisać szkic, uzupełnij wymagane dane konta.',
                });
            }
            return false;
        }

        const payload = this.buildSettingsPayload();
        const payloadKey = JSON.stringify(payload);
        if (this.hasPersistedDraft && payloadKey === this.lastPersistedSettingsKey) {
            return true;
        }

        this.savingDraft = true;
        try {
            const response = this.hasPersistedDraft
                ? await api.updateSessionAgreementDraft(this.sessionId, payload)
                : await api.createSessionAgreementDraft(this.sessionId, payload);

            this.suppressAutosave = true;
            this.applySettings(response.settings);
            this.hasPersistedDraft = true;
            this.renderedTextSnapshot = response.renderedTextSnapshot || null;
            this.lastPersistedSettingsKey = JSON.stringify(response.settings || {});
            this.suppressAutosave = false;

            if (!silent) {
                (this as any).$vToastify.success({
                    title: 'Zapisano',
                    body: 'Szkic zgody został zapisany.',
                });
            }

            return true;
        } catch (e) {
            this.suppressAutosave = false;
            if (!silent) {
                (this as any).$vToastify.error({
                    title: 'Błąd',
                    body: 'Nie udało się zapisać szkicu zgody.',
                });
            }
            return false;
        } finally {
            this.savingDraft = false;
        }
    }

    private async fetchRenderedPreview(): Promise<void> {
        if (!this.canOpenAgreementForm || this.renderPreviewLoading) {
            return;
        }

        this.renderPreviewLoading = true;
        try {
            const payload = this.buildSettingsPayload();
            this.consentRenderedContent = await api.renderAgreementPreview(this.sessionId, payload);
        } catch {
            this.consentRenderedContent = '<p style="color:red;">Nie udało się wygenerować podglądu zgody.</p>';
        } finally {
            this.renderPreviewLoading = false;
        }
    }

    async openConsentModal() {
        if (!this.canOpenAgreementForm) {
            (this as any).$vToastify.warning({
                title: 'Uzupełnij dane',
                body: 'Aby wyświetlić treść, uzupełnij wymagane dane konta.',
            });
            return;
        }

        this.showConsentModal = true;
        await this.fetchRenderedPreview();
    }

    @Watch('agreeSocial')
    @Watch('agreeEmail')
    @Watch('agreeSms')
    @Watch('agreePhysical')
    @Watch('agreeWebsite')
    @Watch('websiteUrl')
    @Watch('customPlatformName')
    @Watch('platforms', { deep: true })
    @Debounce(700)
    async onDraftFormChanged() {
        if (this.hydratingDraft || this.loadingDraft || this.suppressAutosave || this.savingDraft) {
            return;
        }
        if (!this.canOpenAgreementForm) {
            return;
        }
        await this.saveDraft(true);
    }

    isValidWebsiteUrl(url: string): boolean {
        if (!url || url.trim() === '') return false;
        
        const trimmedUrl = url.trim().toLowerCase();
        
        // Remove common prefixes to get the core domain
        let domain = trimmedUrl
            .replace(/^https?:\/\//, '') // Remove http:// or https://
            .replace(/^www\./, ''); // Remove www.
        
        // Check if it contains at least a domain with a dot (e.g., "example.com")
        const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/;
        
        // Also allow domains with paths (e.g., "example.com/page")
        const domainWithPathPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}(\/.*)?$/;
        
        return domainPattern.test(domain) || domainWithPathPattern.test(domain);
    }

    hasValidPlatformSelected(): boolean {
        return Object.keys(this.platforms).some(key => {
            if (key === 'inna') {
                return this.platforms.inna && this.customPlatformName.trim() !== '';
            }
            return this.platforms[key as keyof typeof this.platforms];
        });
    }
}
